/**
 * Bulletins, the epoch half.
 *
 * "Show again" reactivates the ORIGINAL announcement doc rather than
 * minting a copy (DECISIONS.md, "Resend reactivates the original, never a
 * copy from the resender"), and it does that WITHOUT rewriting the acks or
 * dismissedBy maps - history stays history. What makes a stale ack stop
 * hiding the reactivated banner is an epoch: bulletins.ts bumps
 * resendEpoch by exactly one on every Show again, and every "has this
 * person acted on it" question here also asks "at THIS epoch or later."
 *
 * An ack or dismissal recorded before this feature existed - or before the
 * bulletin's most recent resend - carries no entry in ackEpochs /
 * dismissEpochs, which reads as epoch 0. That is what lets old data go
 * unmigrated: a bulletin that has never been shown again has
 * resendEpoch 0, so an epoch-0 ack still hides it exactly as it always did.
 *
 * Pure on purpose: no React, no Firebase, so `node --test` can reach this
 * arithmetic directly instead of standing up a browser or an emulator.
 * bulletins.ts re-exports all of it, so callers never need to know there
 * are two files.
 */

export interface BulletinEpochFields {
  acks?: Record<string, unknown>;
  dismissedBy?: Record<string, unknown>;
  ackEpochs?: Record<string, number>;
  dismissEpochs?: Record<string, number>;
  resendEpoch?: number;
  reactions?: Record<string, unknown>;
}

/**
 * The epoch at which uid's entry in `actedMap` was recorded, or -1 if
 * there is no entry at all (never acted). A present entry with no matching
 * epoch record reads as epoch 0 - see the file doc comment above.
 */
function actedAtEpoch(
  actedMap: Record<string, unknown> | undefined,
  epochMap: Record<string, number> | undefined,
  uid: string
): number {
  if (!actedMap?.[uid]) return -1;
  return epochMap?.[uid] ?? 0;
}

export function bulletinAckedAtCurrentEpoch(
  b: Pick<BulletinEpochFields, 'acks' | 'ackEpochs' | 'resendEpoch'>,
  uid: string | undefined
): boolean {
  if (!uid) return false;
  return actedAtEpoch(b.acks, b.ackEpochs, uid) >= (b.resendEpoch ?? 0);
}

export function bulletinDismissedAtCurrentEpoch(
  b: Pick<BulletinEpochFields, 'dismissedBy' | 'dismissEpochs' | 'resendEpoch'>,
  uid: string | undefined
): boolean {
  if (!uid) return false;
  return actedAtEpoch(b.dismissedBy, b.dismissEpochs, uid) >= (b.resendEpoch ?? 0);
}

/**
 * Did uid tap an emoji on this announcement at its CURRENT Show again round?
 *
 * A reaction keeps no round of its own. The reacting surfaces stamp the ack
 * and its round in the same write, so a reaction reads as the round of her
 * ack stamp, and a reaction with no stamp at all (one from before an emoji
 * also acknowledged) reads as the first round, the way an ack with no round
 * does (see the file doc comment above). So an emoji from before a Show
 * again never answers the new round.
 */
export function bulletinReactedAtCurrentEpoch(
  b: Pick<BulletinEpochFields, 'reactions' | 'ackEpochs' | 'resendEpoch'>,
  uid: string | undefined
): boolean {
  if (!uid || !b.reactions?.[uid]) return false;
  return (b.ackEpochs?.[uid] ?? 0) >= (b.resendEpoch ?? 0);
}

/**
 * Whether uid has already answered this bulletin - acknowledged it,
 * dismissed it, or tapped an emoji on it - AT THE BULLETIN'S CURRENT
 * resendEpoch. An answer from before the latest Show again no longer
 * counts, which is the whole point: it is what lets the reactivated banner
 * reach someone who already handled the original without anyone rewriting
 * what they did the first time.
 *
 * Her emoji counts on its own, stamped or not (Bobbie, 2026-10-25, decision
 * card, her pick "Count as acknowledged (Recommended)"): a family who tapped
 * one before an emoji also stamped the ack HAS acknowledged the
 * announcement, so it files to Past for her like an acknowledged one and
 * stops asking ("The same tap must never be demanded twice", her 2026-08-18
 * rule in DECISIONS.md).
 */
export function bulletinActedOnBy(
  b: Pick<BulletinEpochFields, 'acks' | 'dismissedBy' | 'ackEpochs' | 'dismissEpochs' | 'resendEpoch' | 'reactions'>,
  uid: string | undefined
): boolean {
  if (!uid) return false;
  return bulletinAckedAtCurrentEpoch(b, uid)
    || bulletinDismissedAtCurrentEpoch(b, uid)
    || bulletinReactedAtCurrentEpoch(b, uid);
}

/**
 * Has she answered this announcement at its current round, on any sign-in
 * she holds (ONE PERSON, HOWEVER SHE SIGNS IN): an ack, a dismissal or her
 * own emoji (bulletinActedOnBy). Messages asks it before it offers Done with
 * this on an announcement row (refute round 3 of Bobbie's 2026-10-25 emoji
 * ruling): that button writes a dismissal, and on an announcement her emoji
 * already answered it would move her from Acknowledged to Dismissed on the
 * staff roster (bulletinRosterFor below).
 */
export function bulletinAnsweredByAnyOf(
  b: Pick<BulletinEpochFields, 'acks' | 'dismissedBy' | 'ackEpochs' | 'dismissEpochs' | 'resendEpoch' | 'reactions'>,
  uids: readonly (string | null | undefined)[]
): boolean {
  return uids.some((uid) => bulletinActedOnBy(b, uid ?? undefined));
}

export interface BulletinAttentionFields extends BulletinEpochFields {
  ackRequired?: boolean;
  expectedRecipientUids?: string[];
}

export type BulletinAttentionBucket = 'live' | 'past' | 'excluded';

/**
 * Where a bulletin belongs in ONE viewer's personal live/Past split -
 * pulled out of useAttentionBulletins (bulletins.ts) so the ruling behind
 * it (DECISIONS.md, "Acknowledged means filed to Past, pinned and
 * expiring included": "even a pinned announcement when somebody's
 * acknowledged it, I think she should still move to past messages, even
 * if there's an expiration date on it") is something a plain node test can
 * drive directly with a fabricated bulletin, pinned or not.
 *
 * `pinned` is deliberately never read here. Pinning holds prominence only
 * for people who have NOT acted yet; this function is exactly what decides
 * "has this person acted," so reading `pinned` here would be the bug the
 * ruling forbids, not a feature of it. An expiresAt in the past is not
 * read here either, on purpose: expiry moves a bulletin into the archived
 * tail (isBulletinArchived, bulletins.ts) for EVERYONE, a different, older
 * ruling; this function only ever answers the per-viewer acted-on question.
 *
 * 'excluded' mirrors the pre-existing skip: an ack-required bulletin not
 * aimed at this uid never shows either way - the write would be denied,
 * so there is nothing this viewer could have acted on.
 */
export function bulletinAttentionBucket(
  b: Pick<
    BulletinAttentionFields,
    'acks' | 'dismissedBy' | 'ackEpochs' | 'dismissEpochs' | 'resendEpoch' | 'ackRequired' | 'expectedRecipientUids' | 'reactions'
  >,
  uid: string
): BulletinAttentionBucket {
  if (b.ackRequired && !(b.expectedRecipientUids ?? []).includes(uid)) return 'excluded';
  return bulletinActedOnBy(b, uid) ? 'past' : 'live';
}

/** Just the announcement fields the per-person answer reads. */
export interface BulletinAnswerFields extends BulletinAttentionFields {
  createdBy?: string | null;
}

/**
 * Does this announcement still ask uid for an acknowledgement? It asks for
 * one, she is on its recipient list, she did not post it, and she has not
 * answered it at the current Show again round (bulletinActedOnBy: an ack, a
 * dismissal or her emoji). The home count, the app badge and Messages ask
 * this through studioAnnouncementOwesAck (messagesUnreadCore.ts), the
 * Announcements page through bulletinMyAnswer below.
 */
export function bulletinAckOwedBy(b: BulletinAnswerFields, uid: string | null | undefined): boolean {
  if (!uid) return false;
  return !!b.ackRequired
    && (b.expectedRecipientUids ?? []).includes(uid)
    && b.createdBy !== uid
    && !bulletinActedOnBy(b, uid);
}

/** Her own answer to one announcement, on one sign-in, as a screen shows it. */
export interface BulletinMyAnswer {
  /** It still asks her: Waiting on you, Understood/complete and Dismiss. */
  owed: boolean;
  /** Show her as having acknowledged it (Understood). */
  acknowledged: boolean;
  /** She dismissed it at this round. */
  dismissed: boolean;
}

/**
 * An ack at this round, or her emoji at this round unless she dismissed it.
 * A dismissal outranks a reaction, the same order the class post roster
 * keeps (pages/classPage/helpers.ts postAckState), so the Director never
 * reads Acknowledged for a family who said not for me.
 */
function bulletinAcknowledgedBy(b: BulletinAnswerFields, uid: string): boolean {
  return bulletinAckedAtCurrentEpoch(b, uid)
    || (bulletinReactedAtCurrentEpoch(b, uid) && !bulletinDismissedAtCurrentEpoch(b, uid));
}

/**
 * Her answer to an announcement, for the Announcements page: the Waiting on
 * you or Understood pill and whether Understood/complete and Got it show.
 * `owed` is bulletinAckOwedBy, the count's own question, so the page never
 * asks for a tap the count has already taken as given.
 */
export function bulletinMyAnswer(b: BulletinAnswerFields, uid: string | null | undefined): BulletinMyAnswer {
  if (!uid) return { owed: false, acknowledged: false, dismissed: false };
  return {
    owed: bulletinAckOwedBy(b, uid),
    acknowledged: bulletinAcknowledgedBy(b, uid),
    dismissed: bulletinDismissedAtCurrentEpoch(b, uid),
  };
}

/** One announcement's acknowledgement roster, for the staff who track it. */
export interface BulletinRoster {
  acknowledged: string[];
  dismissed: string[];
  /** Everyone asked who has not answered at this round. */
  stillNeeded: string[];
}

/**
 * Who has acknowledged, dismissed, or still needs to answer, at the current
 * round. Her emoji is her acknowledgement here too (Bobbie, 2026-10-25), so
 * a family the app tells she has acknowledged is never listed under Still
 * needed.
 */
export function bulletinRosterFor(b: BulletinAnswerFields): BulletinRoster {
  const expected = b.expectedRecipientUids ?? [];
  return {
    acknowledged: expected.filter((uid) => bulletinAcknowledgedBy(b, uid)),
    dismissed: expected.filter((uid) => bulletinDismissedAtCurrentEpoch(b, uid)),
    stillNeeded: expected.filter((uid) => !bulletinActedOnBy(b, uid)),
  };
}
